import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import BlockIcon from '@mui/icons-material/Block'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'

import { formatarTelefone, motivoSuficiente, telefoneValido } from '../Bloqueios.presenter'
import { Bloqueio, BLOQUEIOS_POR_PAGINA, BloqueiosScope, MOTIVO_MINIMO, SituacaoMeta } from '../Bloqueios.scopes'

const LOG = Logger.get('Bloqueios-VIEW')

const numero = (n: number) => n.toLocaleString('pt-BR')
const quando = (iso?: string) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'

const SITUACAO_META: Record<SituacaoMeta, { rotulo: string; cor: 'success' | 'warning' | 'error' | 'default' }> = {
    bloqueado: { rotulo: 'Bloqueado na Meta', cor: 'success' },
    simulado: { rotulo: 'Simulado (desenvolvimento)', cor: 'default' },
    pendente: { rotulo: 'Aguardando a Meta', cor: 'warning' },
    falhou: { rotulo: 'Meta recusou', cor: 'error' },
    desbloqueado: { rotulo: 'Desbloqueado', cor: 'default' }
}

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(2.5),
        // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
        overflow: 'auto',
        minHeight: 0,
        // Sem isto os cards encolhem e recortam o conteúdo (o Card do MUI tem overflow: hidden).
        '& > *': {
            flexShrink: 0
        }
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    acoesCabecalho: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center'
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    tituloCard: {
        fontWeight: 600,
        fontSize: 15
    },
    nota: {
        fontSize: 13,
        color: theme.palette.text.secondary
    },
    filtros: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: theme.spacing(1.5)
    },
    telefone: {
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
    },
    secundario: {
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    acoes: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
    }
}))

export type BloqueiosViewProps = {
    className?: string
    scope: BloqueiosScope
}

class BloqueiosClass implements FCClassContext<BloqueiosViewProps> {
    scope!: BloqueiosScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitAtualizar = () => this.scope.onAtualizar().catch(LOG.caught)
    readonly emitSomenteAtivos = (_evt: unknown, valor: boolean | null) => {
        if (valor != null) this.scope.onSomenteAtivosChange(valor).catch(LOG.caught)
    }
    readonly emitBusca = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onBuscaChange(evt.target.value).catch(LOG.caught)
    readonly emitPagina = (_evt: unknown, pagina: number) => this.scope.onPaginaChange(pagina).catch(LOG.caught)

    readonly emitAbrirBloquear =
        (telefone = '') =>
        () =>
            this.scope.onAbrirBloquear(telefone).catch(LOG.caught)
    readonly emitFecharBloquear = () => this.scope.onFecharBloquear().catch(LOG.caught)
    readonly emitTelefoneBloquear = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onTelefoneBloquearChange(evt.target.value).catch(LOG.caught)
    readonly emitMotivoBloquear = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMotivoBloquearChange(evt.target.value).catch(LOG.caught)
    readonly emitConfirmarBloquear = () => this.scope.onConfirmarBloquear().catch(LOG.caught)

    readonly emitTentarNaMeta = (telefone: string) => () => this.scope.onTentarNaMeta(telefone).catch(LOG.caught)

    readonly emitAbrirDesbloquear = (telefone: string) => () =>
        this.scope.onAbrirDesbloquear(telefone).catch(LOG.caught)
    readonly emitFecharDesbloquear = () => this.scope.onFecharDesbloquear().catch(LOG.caught)
    readonly emitMotivoDesbloquear = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onMotivoDesbloquearChange(evt.target.value).catch(LOG.caught)
    readonly emitConfirmarDesbloquear = () => this.scope.onConfirmarDesbloquear().catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: BloqueiosViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: BloqueiosViewProps) {
        const { classes } = this.styles

        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Bloqueio de números
                        </Typography>
                        <Typography color="text.secondary" fontSize={15}>
                            Corte o WhatsApp da LIA para números que abusam do canal, como robôs de consulta.
                        </Typography>
                    </Box>
                    <Box className={classes.acoesCabecalho}>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={this.emitAtualizar}>
                            Atualizar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<BlockIcon />}
                            onClick={this.emitAbrirBloquear()}
                        >
                            Bloquear número
                        </Button>
                    </Box>
                </Box>

                <Alert severity="info">
                    O bloqueio vale na hora no servidor da LIA, que passa a descartar as mensagens do número sem
                    responder, e é pedido também à Meta, para que elas nem cheguem. A Meta só aceita bloquear quem
                    escreveu para a LIA nas últimas 24 horas.
                </Alert>

                {this.renderSuspeitos()}
                {this.renderBloqueados()}
                {this.renderDialogoBloquear()}
                {this.renderDialogoDesbloquear()}
            </div>
        )
    }

    renderSuspeitos() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Card className={classes.card}>
                {scope.carregandoSuspeitos && <LinearProgress />}
                <CardContent>
                    <Typography className={classes.tituloCard}>Quem mais escreveu nas últimas 24 horas</Typography>
                    <Typography className={classes.nota}>
                        Números que a LIA recusou por não serem de parceiro (contados desde que o servidor subiu) e
                        parceiros com 200 consultas ou mais.
                    </Typography>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Telefone</TableCell>
                                    <TableCell>De onde</TableCell>
                                    <TableCell align="right">Mensagens</TableCell>
                                    <TableCell>Última</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.suspeitos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>Ninguém insistindo nas últimas 24 horas.</TableCell>
                                    </TableRow>
                                )}
                                {scope.suspeitos.map((s) => (
                                    <TableRow key={s.telefone}>
                                        <TableCell>
                                            <span className={classes.telefone}>{formatarTelefone(s.telefone)}</span>
                                            {s.parceiro && <div className={classes.secundario}>{s.parceiro}</div>}
                                        </TableCell>
                                        <TableCell>
                                            {s.origem === 'recusadas' ? 'Recusadas pela LIA' : 'Consultas de CNPJ'}
                                        </TableCell>
                                        <TableCell align="right">{numero(s.mensagens)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{quando(s.ultima)}</TableCell>
                                        <TableCell align="right">
                                            {s.bloqueado ? (
                                                <Chip size="small" label="Bloqueado" />
                                            ) : (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={this.emitAbrirBloquear(s.telefone)}
                                                >
                                                    Bloquear
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        )
    }

    renderBloqueados() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Card className={classes.card}>
                {scope.carregandoLista && <LinearProgress />}
                <CardContent>
                    <Typography className={classes.tituloCard}>Números bloqueados</Typography>
                    <Typography className={classes.nota}>
                        Mensagens descartadas: as que chegaram ao servidor depois do bloqueio. Param de subir quando a
                        Meta passa a bloquear.
                    </Typography>
                    <Box className={classes.filtros}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={scope.somenteAtivos}
                            onChange={this.emitSomenteAtivos}
                            aria-label="Quais bloqueios"
                        >
                            <ToggleButton value={true} sx={{ textTransform: 'none' }}>
                                Bloqueados agora
                            </ToggleButton>
                            <ToggleButton value={false} sx={{ textTransform: 'none' }}>
                                Todos, com os desbloqueados
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            size="small"
                            sx={{ flex: '1 1 260px', maxWidth: 380 }}
                            placeholder="Buscar por telefone, motivo ou parceiro"
                            value={scope.busca}
                            onChange={this.emitBusca}
                            inputProps={{ 'aria-label': 'Buscar bloqueio' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Telefone</TableCell>
                                    <TableCell>Motivo</TableCell>
                                    <TableCell>Bloqueado</TableCell>
                                    <TableCell>Na Meta</TableCell>
                                    <TableCell align="right">Descartadas</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.bloqueios.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>Nenhum número bloqueado.</TableCell>
                                    </TableRow>
                                )}
                                {scope.bloqueios.map((b) => this.renderLinha(b))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.total}
                        page={scope.pagina}
                        onPageChange={this.emitPagina}
                        rowsPerPage={BLOQUEIOS_POR_PAGINA}
                        rowsPerPageOptions={[BLOQUEIOS_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>
        )
    }

    renderLinha(b: Bloqueio) {
        const { scope } = this
        const { classes } = this.styles
        const situacao = SITUACAO_META[b.metaSituacao] ?? SITUACAO_META.pendente
        const chip = <Chip size="small" color={situacao.cor} variant="outlined" label={situacao.rotulo} />

        return (
            <TableRow key={b.telefone} sx={b.ativo ? undefined : { opacity: 0.65 }}>
                <TableCell>
                    <span className={classes.telefone}>{formatarTelefone(b.telefone)}</span>
                    {b.parceiro && <div className={classes.secundario}>{b.parceiro}</div>}
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                    {b.motivo}
                    {!b.ativo && b.motivoDesbloqueio && (
                        <div className={classes.secundario}>
                            Desbloqueado por {b.desbloqueadoPor} em {quando(b.desbloqueadoEm)}: {b.motivoDesbloqueio}
                        </div>
                    )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {quando(b.bloqueadoEm)}
                    <div className={classes.secundario}>por {b.bloqueadoPor}</div>
                </TableCell>
                <TableCell>{b.metaErro ? <Tooltip title={b.metaErro}>{chip}</Tooltip> : chip}</TableCell>
                <TableCell align="right">
                    {numero(b.mensagensDescartadas)}
                    {b.ultimaMensagemEm && (
                        <div className={classes.secundario}>última {quando(b.ultimaMensagemEm)}</div>
                    )}
                </TableCell>
                <TableCell>
                    {b.ativo && (
                        <Box className={classes.acoes}>
                            {(b.metaSituacao === 'falhou' || b.metaSituacao === 'pendente') && (
                                <Button
                                    size="small"
                                    onClick={this.emitTentarNaMeta(b.telefone)}
                                    disabled={scope.salvando}
                                >
                                    Tentar na Meta
                                </Button>
                            )}
                            <Button
                                size="small"
                                onClick={this.emitAbrirDesbloquear(b.telefone)}
                                disabled={scope.salvando}
                            >
                                Desbloquear
                            </Button>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        )
    }

    renderDialogoBloquear() {
        const { scope } = this
        const podeConfirmar =
            telefoneValido(scope.telefoneBloquear) && motivoSuficiente(scope.motivoBloquear) && !scope.salvando

        return (
            <Dialog open={scope.bloquearAberto} onClose={this.emitFecharBloquear} fullWidth maxWidth="sm">
                <DialogTitle>Bloquear número</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        A LIA deixa de receber e de responder as mensagens desse número. Se ele for de um parceiro, o
                        parceiro fica sem o canal até o desbloqueio.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Telefone com DDD"
                        placeholder="(48) 99999-8888"
                        value={scope.telefoneBloquear}
                        onChange={this.emitTelefoneBloquear}
                        error={!!scope.erroBloquear}
                        helperText={scope.erroBloquear}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Motivo"
                        placeholder="Por exemplo: robô consultando CNPJs sem parar; parceiro já desabilitado."
                        value={scope.motivoBloquear}
                        onChange={this.emitMotivoBloquear}
                        helperText={`Fica registrado com o seu usuário. Pelo menos ${MOTIVO_MINIMO} caracteres.`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharBloquear} disabled={scope.salvando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={this.emitConfirmarBloquear}
                        disabled={!podeConfirmar}
                    >
                        {scope.salvando ? 'Bloqueando…' : 'Bloquear'}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    renderDialogoDesbloquear() {
        const { scope } = this
        const bloqueio = scope.desbloquearAberto

        return (
            <Dialog open={!!bloqueio} onClose={this.emitFecharDesbloquear} fullWidth maxWidth="sm">
                <DialogTitle>Desbloquear {bloqueio ? formatarTelefone(bloqueio.telefone) : ''}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        A LIA volta a receber e a responder as mensagens desse número, inclusive na Meta.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label="Motivo"
                        value={scope.motivoDesbloquear}
                        onChange={this.emitMotivoDesbloquear}
                        helperText={`Fica registrado com o seu usuário. Pelo menos ${MOTIVO_MINIMO} caracteres.`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharDesbloquear} disabled={scope.salvando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={this.emitConfirmarDesbloquear}
                        disabled={!motivoSuficiente(scope.motivoDesbloquear) || scope.salvando}
                    >
                        Desbloquear
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}

export const BloqueiosView = classToFComponent(BloqueiosClass, React)
export default BloqueiosView
